<template>
    <div class="plottable-chart detail-table">
        <loader class="panel-loader" v-if="!isDataLoaded"></loader>
        <div class="chart-container" v-if="isDataLoaded && !hasError">
            <div class="chart-controls">
                <div class="breadcrumb-container">
                    <ul class="breadcrumb" v-if="dataZoom.length || detailView">
                        <li v-on:click="dataZoomReset()" class="clickable">Top</li>
                        <li v-for="(zoom,index) in dataZoom" v-bind:class="breadcrumbClickable(index)" v-on:click="breadcrumbZoomOut(index)">{{parentLabel(zoom)}}</li>
                        <li class="clickable" v-on:click="hideDetails()" v-show="detailView">Back</li>
                    </ul>
                </div>
                <div class="filter" v-if="hasFilter">
                    <template v-for="(filterValue, filterName) in filter">
                        <select class="form-control" v-model="filter[filterName]" v-on:change="getData()">
                            <option v-for="filterOption in filterOptions[filterName]" v-bind:value="filterOption.value">{{filterOption.label}}</option>
                        </select>
                    </template>
                </div>
            </div>
            <div class="table-data" v-show="!detailView">
                <table class="table">
                    <thead><tr><th v-for="field in content['tableMetaData']">{{field.label || field}}</th></tr></thead>
                    <tbody>
                        <tr v-for="(row,index) in filteredTableData()" class="clickable" v-on:click="zoomIn(row['id'],index)">
                            <td v-for="field in trimRow(row, content['tableMetaData'])">{{field}}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="detailed-data" v-show="detailView">
                <h4>{{implode(detailLabel, ', ')}}</h4>
                <dl>
                    <template v-for="(field,index) in content['detailMetaData']">
                        <dt>{{field.label || field}}</dt>
                        <dd v-for="value in getDetailField(index, field.format || index)">
                            <template v-if="hasDetailFieldLink(field)">
                                <a v-bind:href="getDetailFieldLink(field)">{{value}}</a>
                            </template>
                            <template v-if="!hasDetailFieldLink(field)">
                                {{value}}
                            </template>
                        </dd>
                    </template>
                </dl>
            </div>
        </div>
        <div class="error-container alert alert-danger" v-if="isDataLoaded && hasError">
            {{error}}
        </div>
        <div class="chart-generation-date" v-if="isDataLoaded && hasGenDate">
             Data generated at: {{content['genDate']}}
        </div>
    </div>
</template>

<script>
    import axios from 'axios'
    import querystring from 'querystring'
    import Loader from '@/components/Loader'
    import ChartMixin from '@/mixins/ChartMixin.js'

    export default {
        props: ['contentSource', 'id'],
        mixins: [ChartMixin],
        name: 'detailTable',
        data () {
            return {
                filter: null,
                filterOptions: [],
                content: null,
                components: {},
                dataSets: {},
                dataZoom: [],
                details: null
            }
        },
        computed: {
            hasFilter () {
                return this.filter !== null
            },
            isDataLoaded () {
                return this.content !== null
            },
            hasError () {
                return this.content.error !== undefined && this.content.error !== null
            },
            hasGenDate () {
                return this.content.genDate !== undefined && this.content.genDate !== null
            },
            error () {
                return this.content.error || 'An unknown error has occurred'
            },
            detailView () {
                return this.details !== null
            },
            detailLabel () {
                return this.detailView ? this.filteredTableData()[this.details].label || 'Details' : ''
            },
            parent () {
                return this.dataZoom[this.dataZoom.length - 1] || 0
            }
        },
        methods: {
            breadcrumbClickable (index) {
                return (this.dataZoom.length - 1 > index) || this.detailView ? 'clickable' : ''
            },
            parentLabel (parent) {
                const data = this.content.data.filter((data) => data.id === parent)
                return data.length ? data[0].label || '' : ''
            },
            setData (data) {
                this.content = data
                this.filter = this.content.filter || null
                this.filterOptions = this.content.filterOptions || []
            },
            getData () {
                this.content = null
                let self = this
                return axios.post(this.contentSource, querystring.stringify(Object.assign({id: this.id}, this.filter)), {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
                    .then(function (response) {
                        if (response.data.result) {
                            self.setData(response.data.content)
                        } else {
                            self.setData({error: response.data.message || 'An unknown error has occurred'})
                        }
                    })
                    .catch((error) => {
                        if (error instanceof Error) {
                            self.setData({error: error.message || 'An unknown error has occurred'})
                        }
                    })
            },
            showDetails (index) {
                this.details = index
            },
            hideDetails () {
                this.details = null
            },
            zoomIn (id, index) {
                if (this.hasChildren(id, this.content.data)) {
                    this.dataZoomIn(this, id)
                } else {
                    this.showDetails(index)
                }
            },
            dataZoomReset () {
                this.dataZoom = []
                this.hideDetails()
                this.redrawChart()
            },
            breadcrumbZoomOut (index) {
                this.hideDetails()
                if (index + 1 < this.dataZoom.length) {
                    this.dataZoomOut(this, this.dataZoom[index + 1])
                }
            },
            filteredTableData () {
                const self = this

                return this.content.data.filter(data => {
                    return data.parent === self.parent
                })
            },
            getDetailField (field, parts) {
                if (this.details === null) {
                    return []
                }

                let fieldValues = this.filteredTableData()[this.details] || []

                fieldValues = Array.isArray(fieldValues) ? fieldValues : [fieldValues]

                if (fieldValues.length === 0) {
                    return []
                }

                let outputValues = []
                for (let fieldValue of fieldValues) {
                    outputValues.push(this.formatValue(fieldValue, parts))
                }

                return outputValues
            },
            hasDetailFieldLink (field) {
                if (typeof field !== 'object') {
                    return false
                }

                return field.link !== undefined && field.link !== null
            },
            getDetailFieldLink (field) {
                if (this.details === null) {
                    return ''
                }

                if (typeof field === 'string' || field.link === undefined || field.link === null) {
                    return ''
                }

                var link = field.link
                var values = this.filteredTableData()[this.details] || []

                for (let key in values) {
                    link = link.replace('{' + key + '}', values[key] || '')
                }

                return link
            }
        },
        created () {
            if (this.contentSource !== undefined && this.contentSource !== null) {
                this.getData()
            }
        },
        components: { Loader }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>

</style>



// WEBPACK FOOTER //
// DetailTable.vue?584699e2